<script>
  import { onMount } from 'svelte';

  import { DataTable, InlineNotification } from 'carbon-components-svelte';
  import isEqual from 'lodash/isEqual';

  import AuditTableAffectedEntityCell from './affected-entity-cell/AuditTableAffectedEntityCell.svelte';
  import AuditTableEventDataCell from './AuditTableEventDataCell.svelte';
  import LoadingSpinner from '../LoadingSpinner.svelte';
  import PaginationWithRouting from '../PaginationWithRouting.svelte';
  import RelativeDate from '../RelativeDate.svelte';

  import { getAuditEvents } from '../../services';

  /** @type {string | null} */
  export let entityId = null,
    entityType,
    queryParams = undefined;

  let pageData = { loading: true };
  let query = { currentPage: 1, pageSize: 25 };
  let previousQuery = { ...query };

  const NOTIFICATIONS = {
    error: {
      kind: 'error',
      title: 'Error:',
      subtitle: 'Failed to load audit events! Please try again later.',
    },
    noAudits: {
      kind: 'info',
      title: 'Info:',
      subtitle: 'There are no audit records for this entity.',
    },
  };

  async function loadAuditEvents() {
    pageData = { ...pageData, loading: true };
    try {
      const { results: auditEvents, count } = await getAuditEvents({
        ...(queryParams ?? { entityId, entityType }),
        limit: query.pageSize,
        offset: (query.currentPage - 1) * query.pageSize,
      });
      // eslint-disable-next-line svelte/infinite-reactive-loop
      pageData = { auditEvents, count, loading: false, displayedNotification: !count && 'noAudits' };
    } catch (error) {
      console.error('[AuditTable] Failed to load audit events!', error);
      // eslint-disable-next-line svelte/infinite-reactive-loop
      pageData = { loading: false, displayedNotification: 'error' };
    }
  }

  onMount(loadAuditEvents);

  $: (async () => {
    if (pageData.loading || isEqual(previousQuery, query)) {
      return;
    }

    previousQuery = { ...query };
    // eslint-disable-next-line svelte/infinite-reactive-loop
    loadAuditEvents();
  })();
</script>

{#if NOTIFICATIONS[pageData.displayedNotification]}
  <InlineNotification hideCloseButton lowContrast {...NOTIFICATIONS[pageData.displayedNotification]} />
{:else}
  <LoadingSpinner loading={pageData.loading}>
    <DataTable
      headers={[
        { key: 'eventLabel', value: 'Event Type' },
        { key: 'eventData', value: 'Changes' },
        ['api-key', 'group', 'user', 'data-feed-metadata'].includes(entityType) && { key: 'affectedEntity', value: 'Affected Entity' },
        { key: 'userId', value: 'Changed By' },
        { key: 'eventTimestamp', value: 'Updated' },
      ].filter(Boolean)}
      rows={pageData.auditEvents ?? []}
    >
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'eventData' && row?.eventData}
          <AuditTableEventDataCell {row} />
        {:else if cell.key === 'affectedEntity'}
          <AuditTableAffectedEntityCell {row} {entityId} {entityType} />
        {:else if cell.key === 'eventTimestamp'}
          <RelativeDate date={cell.value} />
        {:else}
          {cell.value || ''}
        {/if}
      </span>
    </DataTable>
    <PaginationWithRouting
      bind:page={query.currentPage}
      bind:pageSize={query.pageSize}
      pageSizes={[10, 25, 50]}
      totalItems={pageData.count ?? 0}
    />
  </LoadingSpinner>
{/if}
